/**
 * @file TravelRbtJobs.jsx
 * @description Guide page at /travel-rbt-jobs. Answers the "travel RBT" search honestly: what travel RBT work
 *   looks like (travel contracts, long drives, relocation roles), what real postings pay and what to check in an
 *   offer (drive time, mileage, guaranteed hours, cancellations), the rules that follow an RBT anywhere (BACB
 *   supervision, a few state registrations), pros and cons, and the real ways to reach a wider area on ABAcases
 *   (wide drive radius, alerts in each area, open to relocating, BCBA remote work + licensed states).
 *   ABAcases is a local case marketplace, not a travel staffing agency; the page never implies travel contracts,
 *   housing or stipends from ABAcases. Sources live in the content notes; every figure here comes from a posting
 *   or primary source read in October 2026. Copy lives in locales `travelRbtJobs` (EN + ES), one language engine.
 *   Same building blocks as the other guides (GuideShared, Disclosure). No review stamp: a human BCBA stamps guides.
 * @module components/TravelRbtJobs
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { T } from "../theme";
import {
  CarIcon, ClockIcon, CalendarIcon, AlertTriangleIcon, HomeIcon, UsersIcon, ShieldIcon, MapPinIcon,
  BriefcaseIcon, CheckCircleIcon, AlertCircleIcon, MapIcon, BellIcon, MonitorIcon,
  ExternalLinkIcon,
} from "./Icons";
import Breadcrumbs from "./Breadcrumbs";
import PublicFooter from "./PublicFooter";
import { FaqDetails } from "./Disclosure";
import { GuideAtAGlance, GuideYourPath } from "./GuideShared";
import { setSignupIntent } from "../lib/signupIntent";
import { useFaqJsonLd, useBreadcrumbJsonLd, useSiteSchema } from "../lib/seoSchema";
import { usePageSeo, seoHref } from "../lib/seo";
import { track, A, ctaMeta } from "../lib/analytics";

const NAVY = "#0F1024";
const NAVY_MID = "#1a1f3c";
const INDIGO = "#4F46E5";
const INDIGO_LT = "#818cf8";
const BORDER = "rgba(226,232,240,0.8)";
const TEXT_MID = "#475569";
const GREEN = "#15803d";
const AMBER = "#b45309";

const PAGE = "travel-rbt-jobs";
const BACB_RBT_URL = "https://www.bacb.com/rbt/";
const DOL_URL = "https://www.dol.gov/agencies/whd/fact-sheets/22-flsa-hours-worked";

const WHAT_CARDS = [
  { Icon: BriefcaseIcon, n: 1 },
  { Icon: CarIcon, n: 2 },
  { Icon: HomeIcon, n: 3 },
];
const PAY_SAMPLES = [1, 2, 3];
const CHECKS = [
  { Icon: ClockIcon, n: 1 },
  { Icon: CarIcon, n: 2 },
  { Icon: CalendarIcon, n: 3 },
  { Icon: AlertTriangleIcon, n: 4 },
  { Icon: HomeIcon, n: 5 },
  { Icon: UsersIcon, n: 6 },
];
const RULES = [
  { Icon: ShieldIcon, n: 1 },
  { Icon: UsersIcon, n: 2 },
  { Icon: MapPinIcon, n: 3 },
];
const PROS = [1, 2, 3, 4, 5];
const CONS = [1, 2, 3, 4, 5, 6];
const WIDE = [
  { Icon: MapIcon, n: 1 },
  { Icon: BellIcon, n: 2 },
  { Icon: HomeIcon, n: 3 },
  { Icon: MonitorIcon, n: 4 },
];
// One list drives the visible FAQ and the FAQPage JSON-LD, so they cannot diverge.
const FAQ_COUNT = [1, 2, 3, 4, 5];

export default function TravelRbtJobs({ onNavigate }) {
  const { t, i18n } = useTranslation();
  const k = (key) => t(`travelRbtJobs.${key}`);
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 760 : false));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 760);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  usePageSeo({
    title: "Travel RBT Jobs | Pay, Rules and What to Check",
    description: "What travel RBT work looks like, what real postings pay, what to check in an offer, and how to find cases across a wider area on ABAcases.",
    canonical: "/travel-rbt-jobs",
  });
  useSiteSchema(); // global Organization + WebSite
  useBreadcrumbJsonLd("bc-jsonld-travel-rbt-jobs", [
    { name: "Home", url: "/" },
    { name: "ABA Resources", url: "/aba-resources" },
    { name: "Travel RBT Jobs", url: "/travel-rbt-jobs" },
  ]);
  useFaqJsonLd("faq-jsonld-travel-rbt-jobs", FAQ_COUNT.map((n) => ({ q: k(`faq${n}Q`), a: k(`faq${n}A`) })));

  // Crawlable internal link (real <a href>; preventDefault keeps SPA nav). Same pattern as the other guides.
  const SeoLink = ({ to, children }) => (
    <a href={seoHref(to)} onClick={(e) => { e.preventDefault(); onNavigate(to); }} style={{ color: INDIGO, fontWeight: 700, textDecoration: "none" }}>{children}</a>
  );
  const ExtLink = ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: INDIGO, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
      <span>{children}</span> <ExternalLinkIcon size={12} color={INDIGO} />
    </a>
  );

  const goJoin = () => {
    track(A.RESOURCE_PAGE_CTA_CLICK, {
      supabaseMeta: { page: `/${PAGE}/` },
      ga4Params: ctaMeta({ pagePath: `/${PAGE}/`, pageType: "resource", ctaLabel: "Turn on alerts for your area", targetPath: "/auth", userLanguage: i18n.language, audience: "resource" }),
    });
    setSignupIntent("alerts");
    onNavigate("auth");
  };

  const sectionPad = isMobile ? "44px 18px" : "64px 48px";
  const maxW = 900;
  const h2 = { fontSize: isMobile ? 23 : 30, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" };
  const lead = { fontSize: 15.5, color: TEXT_MID, lineHeight: 1.7, margin: "0 0 20px" };
  const alt = { background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad };

  // Icon card: icon tile + bold title + short body. Stacks to one column on phones.
  const Card = ({ Icon, title, body, white = true }) => (
    <div style={{ display: "flex", gap: 14, alignItems: "flex-start", background: white ? "#fff" : T.surfaceAlt, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "18px 18px" }}>
      <div style={{ width: 40, height: 40, borderRadius: 10, background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={18} color={INDIGO} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: NAVY, marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 14, color: TEXT_MID, lineHeight: 1.6 }}>{body}</div>
      </div>
    </div>
  );

  const IconList = ({ title, items, Icon, color, bg }) => (
    <div style={{ background: bg, border: `1px solid ${BORDER}`, borderRadius: 14, padding: isMobile ? "18px 18px" : "22px 24px" }}>
      <h3 style={{ fontSize: 18, fontWeight: 900, color, margin: "0 0 12px" }}>{title}</h3>
      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 10 }}>
        {items.map((it) => (
          <li key={it} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 14.5, color: NAVY, lineHeight: 1.5 }}>
            <Icon size={17} color={color} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div style={{ flex: 1, overflowY: "auto", background: "#fff", fontFamily: T.fontBody }}>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <header style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 100%)`, padding: isMobile ? "28px 18px 40px" : "32px 48px 60px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -120, right: "10%", width: 420, height: 420, borderRadius: "50%", background: "rgba(79,70,229,0.16)", filter: "blur(90px)", pointerEvents: "none" }} />
        <div style={{ position: "relative", maxWidth: maxW, margin: "0 auto" }}>
          <Breadcrumbs items={[
            { label: k("breadcrumbHome"), onClick: () => onNavigate("home") },
            { label: k("breadcrumbResources"), onClick: () => onNavigate("aba-resources") },
            { label: k("breadcrumbCurrent") },
          ]} />
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <span style={{ fontSize: 11.5, fontWeight: 800, color: INDIGO_LT, background: "rgba(255,255,255,0.1)", borderRadius: 7, padding: "4px 10px", letterSpacing: "0.02em" }}>{k("badgeGuide")}</span>
            <span style={{ fontSize: 12, color: INDIGO_LT, fontWeight: 600 }}>abacases.com</span>
          </div>
          <h1 style={{ fontSize: isMobile ? 27 : 40, fontWeight: 900, color: "#fff", margin: "0 0 12px", lineHeight: 1.14, letterSpacing: "-0.025em", maxWidth: 760 }}>
            {k("heroTitle")}
          </h1>
          <p style={{ fontSize: isMobile ? 16 : 19, fontWeight: 600, color: "#e2e8f0", lineHeight: 1.4, maxWidth: 700, margin: "0 0 12px" }}>{k("heroSubtitle")}</p>
          <p style={{ fontSize: isMobile ? 15 : 16.5, color: "#cbd5e1", lineHeight: 1.6, maxWidth: 640, margin: "0 0 26px" }}>{k("heroBody")}</p>
          <button onClick={goJoin} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, minHeight: 48, padding: isMobile ? "13px 24px" : "15px 30px", background: `linear-gradient(135deg, ${INDIGO} 0%, #6366F1 100%)`, color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: "pointer", boxShadow: "0 6px 24px rgba(79,70,229,0.4)" }}>
            <BellIcon size={16} color="#fff" /> {t("guideShared.ctaAlerts")}
          </button>
        </div>
      </header>

      {/* ── Short answer ─────────────────────────────────────── */}
      <GuideAtAGlance
        isMobile={isMobile}
        title={k("glanceTitle")}
        items={[1, 2, 3, 4, 5].map((n) => k(`glanceItem${n}`))}
        confirmPre={k("glanceConfirm")}
        confirmHref={BACB_RBT_URL}
        confirmLabel="bacb.com/rbt"
      />

      {/* ── 1. What travel RBT work looks like ───────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={h2}>{k("whatTitle")}</h2>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 14 }}>
            {WHAT_CARDS.map((c) => <Card key={c.n} Icon={c.Icon} title={k(`whatCard${c.n}Title`)} body={k(`whatCard${c.n}Body`)} white={false} />)}
          </div>
        </div>
      </section>

      {/* ── 2. Pay and what to check ─────────────────────────── */}
      <section style={alt}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={h2}>{k("payTitle")}</h2>
          <p style={lead}>{k("payIntro")}</p>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 14, marginBottom: 10 }}>
            {PAY_SAMPLES.map((n) => (
              <div key={n} data-testid="pay-sample" style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "18px 18px" }}>
                <div style={{ fontSize: 12.5, fontWeight: 800, color: INDIGO, letterSpacing: "0.03em", textTransform: "uppercase", marginBottom: 6 }}>{k(`paySample${n}Label`)}</div>
                <div style={{ fontSize: 20, fontWeight: 900, color: NAVY, lineHeight: 1.25, marginBottom: 8 }}>{k(`paySample${n}Figure`)}</div>
                <div style={{ fontSize: 14, color: TEXT_MID, lineHeight: 1.6 }}>{k(`paySample${n}Body`)}</div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 13, color: TEXT_MID, lineHeight: 1.6, margin: "0 0 34px" }}>{k("paySamplesNote")}</p>

          <h2 style={{ ...h2, fontSize: isMobile ? 22 : 28 }}>{k("checkTitle")}</h2>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 14, marginBottom: 20 }}>
            {CHECKS.map((c) => <Card key={c.n} Icon={c.Icon} title={k(`check${c.n}Title`)} body={k(`check${c.n}Body`)} />)}
          </div>
          <p style={{ fontSize: 15, color: TEXT_MID, lineHeight: 1.7, margin: "0 0 8px" }}>
            {k("checkTrackerPre")} <SeoLink to="free-aba-timesheet-payment-tracker">{k("checkTrackerLink")}</SeoLink>.{" "}
            <SeoLink to="how-to-choose-an-aba-case">{k("checkChooseLink")}</SeoLink>
          </p>
          <p style={{ fontSize: 13, color: TEXT_MID, lineHeight: 1.6, margin: 0 }}>
            {k("checkSourcePre")} <ExtLink href={DOL_URL}>{k("checkSourceLink")}</ExtLink>
          </p>
        </div>
      </section>

      {/* ── 3. Rules that follow you ─────────────────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={h2}>{k("rulesTitle")}</h2>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 14, marginBottom: 18 }}>
            {RULES.map((c) => <Card key={c.n} Icon={c.Icon} title={k(`rulesCard${c.n}Title`)} body={k(`rulesCard${c.n}Body`)} white={false} />)}
          </div>
          <p style={{ fontSize: 15, color: NAVY, fontWeight: 600, lineHeight: 1.6, margin: 0 }}>
            {k("rulesFooterPre")} <ExtLink href={BACB_RBT_URL}>bacb.com/rbt</ExtLink>.
          </p>
        </div>
      </section>

      {/* ── 4. Pros and cons ─────────────────────────────────── */}
      <section style={alt}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={h2}>{k("prosConsTitle")}</h2>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 14 }}>
            <IconList title={k("prosTitle")} items={PROS.map((n) => k(`pros${n}`))} Icon={CheckCircleIcon} color={GREEN} bg="#fff" />
            <IconList title={k("consTitle")} items={CONS.map((n) => k(`cons${n}`))} Icon={AlertCircleIcon} color={AMBER} bg="#fff" />
          </div>
        </div>
      </section>

      {/* ── 5. Wider-area cases on ABAcases ──────────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={h2}>{k("wideTitle")}</h2>
          <p style={lead}>{k("wideIntro")}</p>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 14, marginBottom: 20 }}>
            {WIDE.map((c) => <Card key={c.n} Icon={c.Icon} title={k(`wideCard${c.n}Title`)} body={k(`wideCard${c.n}Body`)} white={false} />)}
          </div>
          <p style={{ fontSize: 15, color: TEXT_MID, lineHeight: 1.7, margin: 0 }}>
            {k("wideLinksPre")} <SeoLink to="aba-jobs-by-state">{k("wideLinkStates")}</SeoLink> {k("wideLinksMid")} <SeoLink to="aba-case-finder">{k("wideLinkFinder")}</SeoLink>.
          </p>
        </div>
      </section>

      {/* ── 6. FAQ ───────────────────────────────────────────── */}
      <section style={{ ...alt, borderBottom: "none" }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <h2 style={{ ...h2, textAlign: "center", margin: "0 0 24px" }}>{k("faqTitle")}</h2>
          {FAQ_COUNT.map((n, i) => <FaqDetails key={n} q={k(`faq${n}Q`)} a={k(`faq${n}A`)} defaultOpen={i === 0} isMobile={isMobile} />)}
        </div>
      </section>

      <div style={{ padding: isMobile ? "20px 18px" : "22px 48px", textAlign: "center", background: "#fff" }}>
        <p style={{ fontSize: 12, color: TEXT_MID, lineHeight: 1.6, maxWidth: 640, margin: "0 auto" }}>{k("sourceNote")}</p>
      </div>

      <GuideYourPath current={PAGE} onNavigate={onNavigate} isMobile={isMobile} />
      <PublicFooter />
    </div>
  );
}
